/* eslint-disable camelcase */
import Link from 'next/link'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import styled, { ThemeContext } from 'styled-components'
// import Head from 'next/head'
import { v4 as uuid } from 'uuid'

import ErrorBox from 'components/ErrorBox'
import PasswordInput from 'components/GlobalStyles/Inputs/PasswordInput'
import TextInput from 'components/GlobalStyles/Inputs/TextInput'
import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'

import { useUser } from 'services/swr/useUser'

import formatLocation from 'utils/formatLocation'
import getSellHomeriseUrl from 'utils/getSellHomeriseUrl'
import { isChrome } from 'utils/userAgent'
import validateEmail from 'utils/validateEmail'

import { ICreateListingData, ICreateUserData, ISignupParams } from 'types'
import { ButtonType } from 'types/enums'

const FullWidthContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
`

const MaxWidthContainer = styled.div`
  flex-basis: 750px;
  flex-shrink: 1;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`

interface ISizeProps {
  size?: number
}
const Spacer = styled.div<ISizeProps>`
  width: ${props => props.size || 20}px;
  height: ${props => props.size || 20}px;
`

const Grid = styled.div`
  /* IE 11 base */
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
`

interface IFlexBasisProps {
  flexBasis?: number
}
interface ISpacerProps {
  spacer?: boolean
}

const Form = styled.form``

const InputContainer = styled.div<IFlexBasisProps & ISpacerProps>`
  /* IE 11 base */
  flex-basis: ${props => `calc(${props.flexBasis || 50}% - ${props.spacer ? 10 : 0}px)`};
  flex-grow: 0;
  margin-bottom: 10px;

  @media (max-width: ${props => props.theme.metrics.phone}px) {
    flex-basis: 100%;
  }
`

interface IErrorProps {
  error: boolean
}
interface IInvalidProps {
  invalid?: boolean
}
interface IDropdownOpenProps {
  dropdownOpen?: boolean
}
const Input = styled.input.attrs({ type: 'text' })<
  IErrorProps & IInvalidProps & IDropdownOpenProps
>`
  padding-top: 2px; /* font correction */
  padding-left: 10px;
  background: ${props => props.theme.colors.transparentGrey30Alpha50};
  ${props => props.error && `background: ${props.theme.colors.transparentRedAlpha10};`}
  border: ${props => `1px solid ${props.theme.colors[props.error ? 'red' : 'grey30']}`};
  border-radius: 4px;
  width: 100%;
  height: 50px;

  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  caret-color: ${props => props.theme.colors.blue};
  color: ${props => props.theme.colors.grey27};

  &::placeholder {
    color: ${props => props.theme.colors.grey27};
    opacity: 1;
  }
  &::-webkit-input-placeholder {
    color: ${props => props.theme.colors.grey27};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border: 1px solid ${props => props.theme.colors[props.invalid ? 'red' : 'blue']};
    ${props =>
    props.dropdownOpen
      && `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
  }
`

const PlaceInputContainer = styled.div`
  position: relative;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 38px;
  left: 0;
  background-color: ${props => props.theme.colors.blue};
  border: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  width: 100%;
  z-index: 100;
  margin: 10px 0px;
  text-align: left;
  letter-spacing: 1px;
  font-size: 16px;

  display: none;

  ${Input}:focus + & {
    display: block;
  }
`

const AutocompleteOption = styled.div`
  padding: 10px 10px 0px;
  font-style: normal;
  font-weight: 900;
  font-size: 11px;
  line-height: 15px;
  letter-spacing: normal;
  color: ${props => props.theme.colors.white};

  &:last-child {
    padding-bottom: 10px;
  }

  cursor: pointer;
`

const InputLabel = styled.label`
  display: block;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  letter-spacing: 1.25px;
  text-align: left;
`

const SmallText = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;

  & > a {
    color: ${props => props.theme.colors.blue};
  }
`

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const NO_GOOGLE_MATCH
  = 'Please retype the address and select one of the Google Maps suggestions. If none of the options match your address or there are no suggestions,'

const placesAutocompleteSearchOptions = {
  componentRestrictions: { country: 'us' },
}

const initialListingData: ICreateListingData = {
  aptNum: '',
  city: '',
  country: '',
  county: '',
  latitude: 0,
  longitude: 0,
  state: '',
  street: '',
  zip: '',
}

const initialCreateUserParams: ISignupParams = {
  user: {
    name: '',
    email: '',
    password: '',
  },
  listing: initialListingData,
}

interface IProps {
  buttonColor?: string
  buttonWidth?: number
}

const CreateListing: FunctionComponent<IProps> = ({ buttonColor, buttonWidth }) => {
  // theme
  const theme = useContext(ThemeContext)

  const renderIcon = () => (
    <FlaredArrow
      fill={theme.colors.white}
      width={20}
      height={14}
    />
  )

  // user (to check if logged in)
  const { user } = useUser()

  // error state
  const [error, setError] = useState('')

  // form state
  const [autoComplete, setAutoComplete] = useState('off')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const mode = isChrome() ? 'nope' : 'off'
    setAutoComplete(mode)
  }, [isChrome])

  const resetErrors = () => {
    if (error) setError('')

    if (submitted) setSubmitted(false)
  }

  useEffect(() => {
    if (error) setSubmitted(false)
  }, [error])

  // user
  const [userData, setUserData] = useState<ICreateUserData>(initialCreateUserParams.user)

  const updateUser = (field: string, value: string) => {
    if (error) setError('')

    if (submitted) setSubmitted(false)

    setUserData({ ...userData, [field]: field === 'email' ? value.trim() : value })
  }

  // listing
  const [listingData, setListingData] = useState<ICreateListingData>(
    initialCreateUserParams.listing,
  )

  const updateListingProperty = (field: string, value: string | number) => {
    if (submitted) setSubmitted(false)

    setListingData({ ...listingData, [field]: value })
  }

  const updateListingObj = async (geocoderResult: google.maps.GeocoderResult) => {
    if (!geocoderResult) return

    const locationData: ICreateListingData | {} = await formatLocation(geocoderResult)
    setListingData({ ...listingData, ...locationData })
  }

  // displayed address
  const [address, setAddress] = useState('')
  const [addressSelected, setAddressSelected] = useState(false)

  const handleAddressChange = (value: string) => {
    setAddress(value)
    setAddressSelected(false)
    resetErrors()
  }

  const handleAddressSelect = async (selectedAddress: string) => {
    try {
      const results = await geocodeByAddress(selectedAddress)

      if (results && !!results[0]) {
        updateListingObj(results[0])
        setAddress(results[0]?.formatted_address)
        setAddressSelected(true)
      }
    } catch (err) {
      setError("We couldn't look up on your address right now. Please try again later.")
    }
  }

  const redirectToSell = () => {
    setSubmitted(true)
    if (!addressSelected || !address) return setError(NO_GOOGLE_MATCH)

    window.location.assign(getSellHomeriseUrl(address))
  }

  const handleSignup = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    redirectToSell()
  }

  const handleCreateListing = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) return

    redirectToSell()
  }

  // google maps script
  // const [loadMapScript, setLoadMapScript] = useState(false)
  // useEffect(() => {
  //   setTimeout(() => {
  //     if (typeof window !== 'undefined' && !window.google) setLoadMapScript(true)
  //   }, 1000)
  // }, [])

  // const renderHead = () => (
  //   <Head>
  //     <script
  //       async
  //       defer
  //       src={`https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_API_KEY}&libraries=places&callback=mapsCallback`}
  //     />
  //   </Head>
  // )

  return (
    <Form onSubmit={user ? handleCreateListing : handleSignup}>
      <FullWidthContainer>
        {/* {loadMapScript && renderHead()} */}
        <MaxWidthContainer>
          <Grid>
            <InputContainer
              flexBasis={75}
              spacer
            >
              <InputLabel>
                Property Address
                <Spacer size={5} />
                <PlacesAutocomplete
                  value={address}
                  onChange={handleAddressChange}
                  onSelect={handleAddressSelect}
                  searchOptions={placesAutocompleteSearchOptions}
                  shouldFetchSuggestions={address.length > 2}
                >
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <PlaceInputContainer>
                      {/*
                    // @ts-ignore */}
                      <Input
                        data-testid="propertyAddress"
                        {...getInputProps({
                          className: 'street-address',
                        })}
                        dropdownOpen={!!suggestions?.length || loading}
                        error={!!error && !addressSelected}
                        name={uuid()} // force browser not to suggest previous inputs
                        autoComplete={autoComplete}
                        disabled={submitted}
                      />
                      <DropdownContainer>
                        {loading && null}
                        {suggestions.map(suggestion => (
                          <AutocompleteOption {...getSuggestionItemProps(suggestion)}>
                            {suggestion.description}
                          </AutocompleteOption>
                        ))}
                      </DropdownContainer>
                    </PlaceInputContainer>
                  )}
                </PlacesAutocomplete>
              </InputLabel>
            </InputContainer>
            <InputContainer
              flexBasis={25}
              spacer
            >
              <InputLabel>
                Unit #
                <Spacer size={5} />
                <TextInput
                  name="aptNum"
                  onChange={updateListingProperty}
                  onChangeImmediate={resetErrors}
                  delay={0}
                  value={listingData.aptNum}
                  inputHeight={50}
                  autoComplete={autoComplete}
                  disabled={submitted}
                />
              </InputLabel>
            </InputContainer>
            {!user && (
              <InputContainer flexBasis={100}>
                <InputLabel>
                  Name
                  <Spacer size={5} />
                  <TextInput
                    name="name"
                    onChange={updateUser}
                    onChangeImmediate={resetErrors}
                    delay={0}
                    value={userData.name}
                    submissionError={!!error && !userData.name}
                    inputHeight={50}
                    autoComplete={autoComplete}
                    disabled={submitted}
                  />
                </InputLabel>
              </InputContainer>
            )}
            {!user && (
              <InputContainer spacer>
                <InputLabel>
                  Email
                  <Spacer size={5} />
                  <TextInput
                    name="email"
                    type="email"
                    onChange={updateUser}
                    onChangeImmediate={resetErrors}
                    delay={0}
                    value={userData.email}
                    submissionError={!!error && !validateEmail(userData.email)}
                    inputHeight={50}
                    autoComplete={autoComplete}
                    disabled={submitted}
                  />
                </InputLabel>
              </InputContainer>
            )}
            {!user && (
              <InputContainer spacer>
                <InputLabel>
                  Password
                  <Spacer size={5} />
                  <PasswordInput
                    name="password"
                    onChange={updateUser}
                    value={userData.password}
                    background={theme.colors.transparentGrey30Alpha50}
                    error={false}
                    submissionError={false}
                    inputHeight={50}
                    disabled={submitted}
                    placeholder=" "
                    textColor={theme.colors.grey27}
                    borderColor={theme.colors.grey30}
                  />
                </InputLabel>
              </InputContainer>
            )}
          </Grid>
          <Spacer size={12} />
          {!!error && (
            <>
              <ErrorBox
                error={error}
                showChatMessage={error === NO_GOOGLE_MATCH}
                chatMessage="please chat with us for help."
                prepopulatedIntercomMessage="None of the Google Maps suggestions match my address/there are no suggestions."
              />
              <Spacer size={32} />
            </>
          )}
          <ButtonContainer>
            <RoundedButton
              type={ButtonType.submit}
              background={buttonColor || theme.colors.blue4}
              color={theme.colors.white}
              disabled={!!error}
              radius={20}
              width={buttonWidth || 300}
              text={user ? 'Create Listing' : 'Create My Free Account'}
              renderRightIcon={renderIcon}
              loading={submitted}
            />
          </ButtonContainer>
          <Spacer size={10} />
          <SmallText>
            {'By creating an account, I agree to the '}
            <Link href="/terms-of-use">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a>Terms &amp; Conditions.</a>
            </Link>
          </SmallText>
          <Spacer size={5} />
          <SmallText>
            {!user && 'Already have an account? '}
            {!user && (
              <Link href="/signin">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a>Sign in here.</a>
              </Link>
            )}
          </SmallText>
        </MaxWidthContainer>
      </FullWidthContainer>
    </Form>
  )
}

export default CreateListing
